import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  serverTimestamp,
  type Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { AreaContact, PlanPartyType } from '../types';
import { mapDocs } from '@utils/firestore';
import { AreaContactSchema } from '../schemas/firestoreSchemas';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:areaContacts');

// Firestore rejects undefined values — organization/role/phone/email/notes
// are all optional on AreaContactSchema.
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

function contactsCollection() {
  return collection(db, COLLECTIONS.AREA_CONTACTS);
}
function contactDoc(id: string) {
  return doc(db, COLLECTIONS.AREA_CONTACTS, id);
}

export interface AreaContactInput {
  name: string;
  organization?: string;
  role?: string;
  partyType: PlanPartyType;
  phone?: string;
  email?: string;
  notes?: string;
}

export const areaContactsApi = {
  // Pre-generates a doc id, same shape as defensePlanPartiesApi.newPartyId
  // and mapOverlaysApi.newOverlayId.
  newContactId(): string {
    return doc(contactsCollection()).id;
  },

  // Moderator-only directory, scoped to one area plus every '*' ("applies
  // everywhere") entry. array-contains-any caps at 30 values, but this only
  // ever passes two, so no truncation logic is needed the way
  // fundingSourcesApi.listFundingSources has it for a caller-supplied list.
  // Sorted by name client-side rather than orderBy('name') server-side, to
  // avoid needing a composite index alongside array-contains-any.
  subscribe(
    areaId: string,
    onNext: (contacts: AreaContact[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(contactsCollection(), where('areaIds', 'array-contains-any', [areaId, '*']));
    return onSnapshotWithRetry(
      q,
      (snap) => {
        const list = mapDocs(snap, AreaContactSchema);
        list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        onNext(list);
      },
      (error) => {
        log.error('subscribe failed:', error);
        onError?.(error);
      }
    );
  },

  async create(
    id: string,
    areaId: string,
    input: AreaContactInput,
    createdBy: string
  ): Promise<void> {
    await setDoc(
      contactDoc(id),
      stripUndefined({
        areaIds: [areaId],
        name: input.name,
        organization: input.organization,
        role: input.role,
        partyType: input.partyType,
        phone: input.phone,
        email: input.email,
        notes: input.notes,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );
  },

  // The caller passes '' (never undefined) for a field it wants cleared —
  // deleteField() actually removes it via updateDoc, where a bare
  // `undefined` would throw client-side. Mirrors
  // defensePlanPartiesApi.updateParty.
  async update(id: string, input: AreaContactInput): Promise<void> {
    await updateDoc(contactDoc(id), {
      name: input.name,
      partyType: input.partyType,
      organization: input.organization || deleteField(),
      role: input.role || deleteField(),
      phone: input.phone || deleteField(),
      email: input.email || deleteField(),
      notes: input.notes || deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(contactDoc(id));
  },
};
